import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

const TokenManager = () => {
  const [tokens, setTokens] = useState([]);
  const [newToken, setNewToken] = useState('');
  const [tokenName, setTokenName] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const savedTokens = JSON.parse(localStorage.getItem('saved_tokens') || '[]');
    setTokens(savedTokens);
    
    const currentToken = localStorage.getItem('telegram_token');
    if (currentToken) {
      const exists = savedTokens.some(t => t.token === currentToken);
      if (!exists) {
        setTokens(prev => [...prev, {
          id: Date.now(),
          name: 'Current Session',
          token: currentToken,
          createdAt: new Date().toISOString(),
          active: true
        }]);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('saved_tokens', JSON.stringify(tokens));
  }, [tokens]);

  const handleAddToken = () => {
    if (!newToken.trim() || !tokenName.trim()) {
      toast.error('Please enter both token name and token');
      return;
    }

    const newTokenObj = {
      id: Date.now(),
      name: tokenName,
      token: newToken,
      createdAt: new Date().toISOString(),
      active: false
    };

    setTokens(prev => [newTokenObj, ...prev]);
    setNewToken('');
    setTokenName('');
    setOpenDialog(false);
    toast.success('Token saved successfully!');
  };

  const handleDeleteToken = (id) => {
    setTokens(prev => prev.filter(token => token.id !== id));
    toast.success('Token removed');
  };

  const handleUseToken = (token) => {
    localStorage.setItem('telegram_token', token);
    
    setTokens(prev => prev.map(t => ({
      ...t,
      active: t.token === token
    })));
    
    toast.success('Token activated! Refreshing...');
    setTimeout(() => window.location.reload(), 1000);
  };

  const handleCopyToken = (token) => {
    navigator.clipboard.writeText(token);
    setCopied(true);
    toast.success('Token copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const currentToken = localStorage.getItem('telegram_token');

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Token Manager
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Add Token
        </Button>
      </Box>

      {currentToken && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          Currently using token ending with: ...{currentToken.slice(-10)}
        </Alert>
      )}

      <Card sx={{ borderRadius: 3, bgcolor: 'background.paper' }}>
        <CardContent>
          {tokens.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <KeyIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography color="textSecondary">
                No tokens saved yet. Add your first token to get started.
              </Typography>
            </Box>
          ) : (
            <List>
              {tokens.map((tokenItem, index) => (
                <React.Fragment key={tokenItem.id}>
                  <ListItem
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      bgcolor: tokenItem.active ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                      border: tokenItem.active ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid transparent',
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <KeyIcon fontSize="small" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {tokenItem.name}
                        </Typography>
                        {tokenItem.active && (
                          <Chip label="Active" color="success" size="small" />
                        )}
                      </Box>
                      <Typography variant="body2" color="textSecondary">
                        Token: ...{tokenItem.token.slice(-15)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Added: {new Date(tokenItem.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                    
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopyToken(tokenItem.token)}
                          title="Copy token"
                        >
                          {copied ? <CheckIcon /> : <CopyIcon />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleUseToken(tokenItem.token)}
                          title="Use this token"
                          color="primary"
                        >
                          <CheckIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteToken(tokenItem.id)}
                          title="Delete token"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < tokens.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon />
            Add New Token
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Token Name"
              value={tokenName}
              onChange={(e) => setTokenName(e.target.value)}
              placeholder="e.g., Work Account, Personal Token"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="API Token"
              value={newToken}
              onChange={(e) => setNewToken(e.target.value)}
              placeholder="Enter your JWT token"
              multiline
              rows={3}
              helperText="Paste your full JWT token here"
            />
            
            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>How to get your token:</strong>
              </Typography>
              <Typography variant="body2">
                1. Login with phone first
              </Typography>
              <Typography variant="body2">
                2. Get token from browser's Developer Tools → Application → Local Storage
              </Typography>
              <Typography variant="body2">
                3. Or use token from previous API response
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleAddToken} variant="contained">
            Save Token
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TokenManager;